import React, { useState, useEffect } from 'react';
import featureFlags, { FeatureFlags } from 'modules/featureFlags';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { MfaStatus } from 'modules/auth/constants';
import { selectOrganisation } from 'modules/organisation/organisationSelectors';
import { selectAppToken, selectEnvironment } from 'modules/global';
import { AuthToken } from 'modules/auth/authToken';
import { useTypedSelector } from 'types/global';
import { matchPropTypes } from 'utils/proptypes';
import { isKSACountry, isUAECountry } from 'config/countriesEnvironments';
import VerifyOwnerDialog from 'components/NoticeBanner/VerifyOwnerDialog';
import config from 'config';
import localhost from 'config/localhost';
import PropTypes from 'prop-types';

import {
  $MfaWarningNotice,
  $VerificationWarningNotice,
  $WarningIcon,
} from './styles';

const DEFAULT_DEPRECATION_CONFIG = { enabled: false, message: '' };
const DEFAULT_DEPRECATION_MESSAGE =
  'New Lean Dashboard experience is available. V1 will be retired on 27 July 2026.';

const NoticeBanner = ({ match, currentUser }) => {
  const { organisation } = useTypedSelector(selectOrganisation);
  const environment = useTypedSelector(selectEnvironment);
  const appToken = useTypedSelector(selectAppToken);

  const [isTeamPage, setIsTeamPage] = useState(false);
  const [shouldVerify, setShouldVerify] = useState(false);
  const [startVerificationFlow, setStartVerificationFlow] = useState(false);

  const userHasEnrolledMfa =
    currentUser.mfa_settings.status !== MfaStatus.notEnrolled;

  const deprecationConfig = useFeatureValue(
    FeatureFlags.rolloutV1DeprecationNotice,
    DEFAULT_DEPRECATION_CONFIG,
  );

  useEffect(() => {
    if (match.path.split('/')[3] === 'team') {
      setIsTeamPage(true);
      setShouldVerify(organisation && !organisation.verified);
    }
  }, [match, organisation]);

  const toggleVerifyDialog = () => {
    setStartVerificationFlow(prev => !prev);
  };

  const handleTryV2DashboardClick = () => {
    const accessToken = AuthToken.get();
    const isLocalEnv = process.env.REACT_APP_V2_ENV === 'localhost';
    const url = isLocalEnv ? localhost.v2DashboardUrl : config.v2DashboardUrl;

    window.open(
      `${url}?${new URLSearchParams({
        access_token: accessToken || '',
        environment: environment || '',
        app_token: appToken || '',
      })}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  //! TODO: for now, only show one banner, so order is important, lets decide on the importance or a new design to show other banners
  const renderTheBanner = () => {
    const showDeprecationBanner = deprecationConfig?.enabled && isUAECountry;

    if (showDeprecationBanner) {
      return (
        <$VerificationWarningNotice onClick={handleTryV2DashboardClick}>
          <$WarningIcon src="/icons/warning.svg" alt="warning icon" />
          <strong>
            <span>
              {deprecationConfig?.message || DEFAULT_DEPRECATION_MESSAGE} &nbsp;
            </span>
          </strong>
          <span>Try it now →</span>
        </$VerificationWarningNotice>
      );
    }

    const shouldRolloutOwnerRole = featureFlags.isOn(
      FeatureFlags.rolloutOwnerRole,
    );
    const shouldRolloutMfa = featureFlags.isOn(FeatureFlags.rolloutMfa);

    const showOwnerVerificationBanner =
      shouldRolloutOwnerRole && isKSACountry && isTeamPage && shouldVerify;

    if (showOwnerVerificationBanner) {
      return (
        <$VerificationWarningNotice onClick={toggleVerifyDialog}>
          <$WarningIcon src="/icons/warning.svg" alt="warning icon" />
          <strong>
            <span>Identity verification required &nbsp;</span>
          </strong>
          <span>
            Please verify your identity to comply with regulatory requirements.
            →
          </span>
        </$VerificationWarningNotice>
      );
    }

    const showMFABanner = shouldRolloutMfa && !userHasEnrolledMfa;

    if (showMFABanner) {
      return (
        <$MfaWarningNotice href="/mfa/setup">
          <div className="caption">
            <$WarningIcon src="/icons/warning.svg" alt="warning icon" />
            <span>MFA is not enabled &nbsp;</span>
          </div>
          <span>Setup an Authenticator app now to protect your account →</span>
        </$MfaWarningNotice>
      );
    }

    return null;
  };

  return (
    <>
      {renderTheBanner()}
      <VerifyOwnerDialog
        isOpen={startVerificationFlow}
        currentUser={currentUser}
        toggleVerifyDialog={toggleVerifyDialog}
      />
    </>
  );
};

NoticeBanner.propTypes = {
  match: matchPropTypes,
  currentUser: PropTypes.shape({
    mfa_settings: PropTypes.shape({
      status: PropTypes.string,
    }),
  }),
};

NoticeBanner.defaultProps = {
  match: {},
  currentUser: {},
};

export default NoticeBanner;
